import type { ContingencyPlanDTO } from '@lmc/ts-common'
import { DefaultLmc, Modal, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from '@lmc/ui-kit/src/layout'
import type { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import { ImageSection } from 'components/generic/ListItem/components'
import { NAMESPACE } from 'locale'
import { consentConstants } from 'locale/consent'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

interface CarePlanLinkingConfirmationModalProps {
  isModalVisible: boolean
  setIsModalVisible: (visible: boolean) => void
  newlyLinkedCarePlans: CarePlanAdlExtended[]
  newlyLinkedContingencyPlans: ContingencyPlanDTO[]
  onConfirm: () => void
}

const CarePlanLinkingConfirmationModal: React.FC<
  CarePlanLinkingConfirmationModalProps
> = ({
  isModalVisible,
  setIsModalVisible,
  newlyLinkedCarePlans,
  newlyLinkedContingencyPlans,
  onConfirm
}) => {
  const { t } = useTranslation(NAMESPACE.CONSENT)

  const handleConfirm = () => {
    onConfirm()
    setIsModalVisible(false)
  }

  return (
    <Modal
      isModalVisible={isModalVisible}
      setIsModalVisible={setIsModalVisible}
      onConfirm={handleConfirm}
      onCancel={handleConfirm}
      localisation={{
        title: t(consentConstants.CARE_PLAN_LINKING_MODAL_TITLE),
        confirmText: 'Confirm'
      }}
      testId="care-plan-linking-confirmation"
    >
      <ContentWrapper>
        <StyledDivider />
        <SuccessText level={2}>
          {t(consentConstants.CARE_PLAN_LINKING_MODAL_SUCCESS_MESSAGE)}
        </SuccessText>

        <NoReviewText level={2}>
          {t(consentConstants.CARE_PLAN_LINKING_MODAL_NO_REVIEW_TRIGGERED)}
        </NoReviewText>
        {newlyLinkedCarePlans.length > 0 && (
          <PlanGroup>
            <PlanGroupTitle level={2}>
              {t(consentConstants.CARE_PLAN_LINKING_MODAL_CARE_PLANS_LABEL)}
            </PlanGroupTitle>
            <CarePlansList>
              {newlyLinkedCarePlans.map((carePlan) => (
                <CarePlanItem key={carePlan.id}>
                  <ImageSection
                    popup
                    iconColor={carePlan.color}
                    icon={carePlan.icon}
                  />
                  <CarePlanName level={2}>{carePlan.name}</CarePlanName>
                </CarePlanItem>
              ))}
            </CarePlansList>
          </PlanGroup>
        )}
        {newlyLinkedContingencyPlans.length > 0 && (
          <PlanGroup>
            <PlanGroupTitle level={2}>
              {t(consentConstants.CARE_PLAN_LINKING_MODAL_FUTURE_PLANS_LABEL)}
            </PlanGroupTitle>
            <CarePlansList>
              {newlyLinkedContingencyPlans.map((plan) => (
                <CarePlanItem key={plan.id}>
                  <FuturePlanIconContainer>
                    <DefaultLmc />
                  </FuturePlanIconContainer>
                  <CarePlanName level={2}>{plan.planName}</CarePlanName>
                </CarePlanItem>
              ))}
            </CarePlansList>
          </PlanGroup>
        )}
      </ContentWrapper>
    </Modal>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const SuccessText = styled(Text)`
  font-size: 16px;
  margin-bottom: 24px;
`

const NoReviewText = styled(Text)`
  font-size: 16px;
  margin-bottom: 24px;
  font-weight: ${theme.font.weight.regular};
`

const StyledDivider = styled(Divider)`
  margin: 0 0 24px 0;
`

const PlanGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`

const PlanGroupTitle = styled(Text)`
  font-size: 14px;
  font-weight: ${theme.font.weight.bold};
  margin-bottom: 8px;
  color: ${theme.color.neutral.color4};
`

const CarePlansList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const CarePlanItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
`

const CarePlanName = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${theme.color.neutral.color3};
`

const FuturePlanIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  margin-right: 5px;
  background-color: ${({ theme }) => theme.color.status.secondary};
  border-radius: 4px;
`

export default CarePlanLinkingConfirmationModal
